import {useRef} from 'react';
import { useTheme } from "@emotion/react";
import PropTypes from "prop-types";
import useBreakpoints from "@util/breakpoints";
import { Header, ButtonPill, Text, Kicker } from "@/base";
import CmsImageLoader from "../base/CmsImageLoader";
import { CacheWrapper } from '@/core/providers/EmotionCacheManager';



const FlexHeroBlade = ({
    text_theme,
    image_size,
    hero_image,
    title,
    sub_title,
    kicker,
    align_copy_horizontal,
    align_copy_vertical,
    copy,
    buttons
}) => {

    const theme = useTheme();
    const delayIncrement = useRef(200)

    // Text theme

    const textDarkColor = {
        color: '#000',
        textShadow: "0 0 1.5rem rgb(255 255 255 / 80%)",
        textAlign: align_copy_horizontal?.value || 'left'
    };

    const textLightColor = {
        color: '#fff',
        textShadow: "0 0 1.5rem rgb(0 0 0 / 80%)",
        textAlign: align_copy_horizontal?.value || 'left'
    };

    const textOptions = [text_theme?.value === "light" ? textLightColor : textDarkColor];

    // KICKER

    const renderKicker = () => {

        delayIncrement.current += 200;

        return (
            <Kicker stylesOverride={textOptions}>
                <div data-aos={direction} data-aos-delay={delayIncrement.current}>{kicker.value}</div>
            </Kicker>
        )
    }

    // TITLE

    const renderTitle = (primary, value) => {

        delayIncrement.current += 200;

         return (
             <div data-aos={direction}
                  data-aos-delay={delayIncrement.current}>
                 <Header tag={primary ? 'h1' : 'h2'} tagStyle={primary ? 'h1' : 'h2'} style={textOptions}>
                     {value}
                 </Header>
             </div>
         )
    }

    // COPY

    const renderCopy = () => {

        delayIncrement.current += 200;

        return (
            <Text stylesOverride={[textOptions, copySize]} 
                  dangerouslySetInnerHTML={{ __html: copy.value }}
                  data-aos={direction}
                  data-aos-delay={delayIncrement.current}/>
        )

    }

    // BUTTONS

    const renderButtons = () => {

        delayIncrement.current += 200;

        const newButton = (button) => {
            return (
                <ButtonPill key={button.id} buttonTheme={button.button_theme.value} to={button.button_url.value}
                            noBorder={true}data-aos={direction} data-aos-delay={delayIncrement.current}
                >{button.button_text.value}</ButtonPill>
            )
        }

        let builtButtons;

        if(Array.isArray(buttons.button)){
            builtButtons = buttons.button.map((button)=>{
                return newButton(button)
            })
        } else {
            builtButtons = buttons.button.button_url.value ? newButton(buttons.button) : null
        }

        if(!builtButtons) return;

        return (
            <span className={`d-flex ${copyPosHorizontal}`}>
                {builtButtons}
            </span>
        )
    }


    const direction = align_copy_horizontal.value === "left" ? "fade-left" : "fade-right";

    const copyPosHorizontal = align_copy_horizontal?.value === "left" ? "justify-content-start" :
        align_copy_horizontal?.value === "center" ? "justify-content-center" : "justify-content-end";

    const copyPosVertical = align_copy_vertical?.value === "top" ? "align-items-start" :
        align_copy_vertical?.value === "middle" ? "align-items-center" : "align-items-end";

    const size = image_size === 'standard' ? '770px' : '660px';

    const alignment = align_copy_horizontal.value === "left" ? {marginRight: 'auto'} : {marginLeft: 'auto'};

    const copySize = useBreakpoints([theme.breakpoint.md])({
        width: ["100%", "45%"],
        ...alignment
    });

    // the primary hero is not quite centered on the left vertically...
    const primaryHeroOffset = align_copy_vertical?.value === "middle" && title?.value ? 'mt-6' : '';

    return (
        <CacheWrapper cacheKey="headCache">
            <div className={`d-flex ${copyPosHorizontal} ${copyPosVertical}`}
                css={{position: "relative", overflow: "hidden", minHeight: size}}>

                {hero_image?.image_url?.value && (
                    <CmsImageLoader className={'img-fill'} url={hero_image.image_url.value} alt={hero_image?.alt_text?.value} />
                )}

                <div className={'posRelative minW-100'}>
                    <div className={`container py-8 ${primaryHeroOffset}`}>

                        {kicker?.value && renderKicker()}

                        {title?.value && renderTitle(true, title.value)}

                        {sub_title?.value && renderTitle(false, sub_title.value)}

                        {copy?.value && renderCopy()}

                        {buttons?.button && renderButtons()}

                    </div>
                </div>

            </div>
        </CacheWrapper>
    );

};

FlexHeroBlade.propTypes = {
    text_theme: PropTypes.object,
    image_size: PropTypes.object,
    hero_image: PropTypes.object,
    title: PropTypes.object,
    kicker: PropTypes.object,
    align_copy_horizontal: PropTypes.object,
    align_copy_vertical: PropTypes.object,
    copy: PropTypes.object,
    buttons: PropTypes.object,
    sub_title: PropTypes.object
};

export default FlexHeroBlade;
